import React, { useEffect, useRef, useState } from 'react';

/**
 * ListDetail — the one `DrillOver` body for `kind` ∈ tracker · history ·
 * done_recently · person (Epic 6 · S6.2, Part A.4 / §4.10).
 *
 * A list of rows that each open an item (the drill STACKS — §4.10: tapping a row
 * pushes the item frame on top of this one; back returns here). The shell
 * (`PortalPage`) fetches the drill payload (`fn_portal_drill(what, id, scope)`)
 * and hands the resolved `{ rows, total, offset }` here; ListDetail itself never
 * imports the data layer (Epic 1).
 *
 * History (and only history) gets a search box (300ms debounce -> the shell
 * re-runs `portalDrill('history', null, {park_id, q, offset:0}, 50)`). Every
 * list kind paginates with Previous / Next driven from the payload's `total` /
 * `offset` (server-side paging — the shell owns the fetch).
 *
 * The list title is the `DrillOver` header (the frame's `label`); ListDetail
 * does NOT repeat it (spec: "label = DrillOver title").
 *
 * Colour (Part C): a single neutral stage chip + a neutral when/done chip; no
 * red/amber. Colour is never the only carrier — every row keeps its title.
 */
const DEBOUNCE_MS = 300;

const RowMeta = ({ row, multiPark }) => (
  <div className="flex flex-wrap items-center gap-2 mt-0.5">
    {multiPark && row?.park && (
      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{row.park}</span>
    )}
    {row?.stage_label && (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
        {row.stage_label}
      </span>
    )}
    {(row?.when || row?.done_at) && (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
        {row.done_at || row.when}
      </span>
    )}
  </div>
);

const ListDetail = ({
  kind,
  result,
  loading = false,
  multiPark = false,
  limit = 50,
  onRowDrill,
  onSearch,
  onPage,
}) => {
  const rows = Array.isArray(result?.rows) ? result.rows : [];
  const total = typeof result?.total === 'number' ? result.total : rows.length;
  const offset = typeof result?.offset === 'number' ? result.offset : 0;
  const isHistory = kind === 'history';

  // Search box — 300ms debounce. Fires onSearch with the trimmed query; an
  // empty query is a reset (q=''), never a missing param.
  const [q, setQ] = useState('');
  const timer = useRef(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const onSearchChange = (e) => {
    const next = e.target.value;
    setQ(next);
    if (typeof onSearch !== 'function') return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearch(next.trim()), DEBOUNCE_MS);
  };

  const hasPrev = offset > 0;
  const hasNext = offset + rows.length < total;
  const prevOffset = Math.max(0, offset - limit);
  const nextOffset = offset + limit;

  return (
    <div className="py-2 space-y-3" data-testid="list-detail" data-kind={kind}>
      {isHistory && (
        <div>
          <label className="sr-only" htmlFor="list-search">Search history</label>
          <input
            id="list-search"
            type="search"
            value={q}
            onChange={onSearchChange}
            placeholder="Search history…"
            aria-label="Search history"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
            data-testid="list-search"
          />
        </div>
      )}

      {loading ? (
        <div className="space-y-2" role="status" aria-label="Loading list">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ))}
          <span className="sr-only">Loading…</span>
        </div>
      ) : rows.length === 0 ? (
        <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
          No records to show for this selection.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800" data-testid="list-rows">
          {rows.map((row, i) => {
            const drillable = Boolean(row?.drill) && typeof onRowDrill === 'function';
            const content = (
              <div className="min-w-0 flex-1">
                <div className="gi-body text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                  {row?.title}
                </div>
                <RowMeta row={row} multiPark={multiPark} />
              </div>
            );
            if (drillable) {
              return (
                <li key={row?.id ?? i}>
                  <button
                    type="button"
                    onClick={() => onRowDrill(row)}
                    data-item-id={row?.id}
                    className="w-full flex items-center gap-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-inset rounded-lg transition-colors"
                  >
                    {content}
                    <svg className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </li>
              );
            }
            return (
              <li key={row?.id ?? i} className="flex items-center gap-3 py-3">
                {content}
              </li>
            );
          })}
        </ul>
      )}

      {/* Previous / Next — server-side paging from total/offset. Hidden when
          there is only one page. Disabled at the ends, never hidden mid-list. */}
      {total > limit && (
        <div className="flex items-center justify-between gap-3 pt-1">
          <button
            type="button"
            onClick={() => onPage?.(prevOffset)}
            disabled={!hasPrev}
            data-testid="list-prev"
            className="inline-flex items-center justify-center min-h-[44px] px-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
          >
            Previous
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums" data-testid="list-range">
            {total === 0 ? 0 : offset + 1}–{Math.min(offset + rows.length, total)} of {total}
          </span>
          <button
            type="button"
            onClick={() => onPage?.(nextOffset)}
            disabled={!hasNext}
            data-testid="list-next"
            className="inline-flex items-center justify-center min-h-[44px] px-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ListDetail;

